import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import apiClient from '@/utils/api';
import { getAuthToken } from '@/utils/auth';
import { saveDraft, getDraft, clearDraft, setPendingAction } from '@/utils/draftManager';
import { toast } from 'sonner';
import { 
  Building2, Home, MapPin, IndianRupee, CheckCircle, 
  Rocket, Star, Crown, ArrowRight, Shield, Users,
  TrendingUp, Target, BarChart3, MessageCircle, Camera, Eye,
  Upload, X, Image as ImageIcon, FileText, Loader2
} from 'lucide-react';
import { useAddonPricing } from '@/hooks/useAddonPricing';
import LocationSearch from '@/components/LocationSearch';
import AreaInput from '@/components/AreaInput';
import { getDefaultUnit } from '@/utils/areaConverter';

// File upload constants
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const ALLOWED_DOC_TYPES = ['application/pdf'];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_DOC_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGES = 10;
const MAX_FLOOR_PLANS = 3;
const MAX_BROCHURES = 2;

// Property subtypes
const propertySubtypes = {
  residential: [
    { value: 'apartment', label: 'Apartment' },
    { value: 'villa', label: 'Villa / House' },
    { value: 'plot', label: 'Plot / Land' },
    { value: 'penthouse', label: 'Penthouse' },
    { value: 'builder_floor', label: 'Builder Floor' },
  ],
  commercial: [
    { value: 'office', label: 'Office Space' },
    { value: 'shop', label: 'Shop / Showroom' },
    { value: 'warehouse', label: 'Warehouse' },
    { value: 'co_working', label: 'Co-working Space' },
  ],
  agricultural: [
    { value: 'farmland', label: 'Farm Land' },
    { value: 'farmhouse', label: 'Farm House' },
    { value: 'agricultural_plot', label: 'Agricultural Plot' },
  ]
};

// Boost tier configuration - icons and styles
const BOOST_ICON_MAP = {
  standard: Rocket,
  premium: Star,
  elite: Crown
};

const BOOST_STYLE_MAP = {
  standard: {
    color: "text-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200"
  },
  premium: {
    color: "text-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200"
  },
  elite: {
    color: "text-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200"
  }
};

// Default boost tiers (fetched from API at runtime)
const DEFAULT_BOOST_TIERS = {
  standard: {
    name: "Standard Boost",
    price: 2999,
    duration: "7 days",
    multiplier: "1.5x",
    features: [
      "1.5x visibility in search results",
      "Featured in category listings",
      "Priority in nearby searches",
      "Basic analytics dashboard"
    ]
  },
  premium: {
    name: "Premium Boost",
    price: 5999,
    duration: "14 days",
    multiplier: "2.5x",
    popular: true,
    features: [
      "2.5x visibility in search results",
      "Featured on homepage carousel",
      "Priority in all location searches",
      "Social media promotion",
      "Advanced analytics dashboard",
      "Dedicated support"
    ]
  },
  elite: {
    name: "Elite Boost",
    price: 9999,
    duration: "30 days",
    multiplier: "4x",
    features: [
      "4x visibility in search results",
      "Premium homepage placement",
      "Targeted email campaigns",
      "WhatsApp broadcast to matched buyers",
      "Professional photo editing",
      "Virtual tour creation",
      "Full analytics suite",
      "Priority 24/7 support"
    ]
  }
};

const ListProperty = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedBoost, setSelectedBoost] = useState(null);
  const [enableBoost, setEnableBoost] = useState(false);
  
  // File upload refs and state
  const imageInputRef = useRef(null);
  const floorPlanInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  
  const [propertyImages, setPropertyImages] = useState([]);
  const [floorPlans, setFloorPlans] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  // Get listing type from URL params
  const typeFromUrl = searchParams.get('type') || 'sale';
  
  // Fetch dynamic pricing from API
  const { boostTiers: apiBoostTiers, loading: pricingLoading } = useAddonPricing();
  
  // Merge API pricing with default config (for styles and icons)
  const boostTiers = Object.keys(DEFAULT_BOOST_TIERS).reduce((acc, key) => {
    acc[key] = {
      ...DEFAULT_BOOST_TIERS[key],
      ...(apiBoostTiers[key] || {}),
      icon: BOOST_ICON_MAP[key],
      ...BOOST_STYLE_MAP[key]
    };
    return acc;
  }, {});
  
  // File upload handlers
  const validateFile = (file, type) => {
    const allowedTypes = type === 'image' ? ALLOWED_IMAGE_TYPES : ALLOWED_DOC_TYPES;
    const maxSize = type === 'image' ? MAX_IMAGE_SIZE : MAX_DOC_SIZE;
    
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Invalid file type. Allowed: ${type === 'image' ? 'JPG, PNG, WebP' : 'PDF'}`);
      return false;
    }
    
    if (file.size > maxSize) {
      toast.error(`File too large. Maximum size: ${type === 'image' ? '5MB' : '10MB'}`);
      return false;
    }
    
    return true;
  };
  
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (propertyImages.length + files.length > MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images allowed`);
      return;
    }
    
    const validFiles = files.filter(file => validateFile(file, 'image'));
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPropertyImages(prev => [...prev, { 
          file, 
          preview: e.target.result,
          id: `img_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    // Reset input
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  
  const handleFloorPlanUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (floorPlans.length + files.length > MAX_FLOOR_PLANS) {
      toast.error(`Maximum ${MAX_FLOOR_PLANS} floor plans allowed`);
      return;
    }
    
    const validFiles = files.filter(file => validateFile(file, 'image') || validateFile(file, 'doc'));
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFloorPlans(prev => [...prev, { 
          file, 
          preview: file.type.startsWith('image/') ? e.target.result : null,
          name: file.name,
          id: `fp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    if (floorPlanInputRef.current) floorPlanInputRef.current.value = '';
  };
  
  const handleBrochureUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (brochures.length + files.length > MAX_BROCHURES) {
      toast.error(`Maximum ${MAX_BROCHURES} brochures allowed`);
      return;
    }
    
    const validFiles = files.filter(file => validateFile(file, 'doc'));
    
    validFiles.forEach(file => {
      setBrochures(prev => [...prev, { 
        file, 
        name: file.name,
        id: `br_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      }]);
    });
    
    if (brochureInputRef.current) brochureInputRef.current.value = '';
  };
  
  const removeImage = (id) => {
    setPropertyImages(prev => prev.filter(img => img.id !== id));
  };
  
  const removeFloorPlan = (id) => {
    setFloorPlans(prev => prev.filter(fp => fp.id !== id));
  };
  
  const removeBrochure = (id) => {
    setBrochures(prev => prev.filter(br => br.id !== id));
  };
  
  const [formData, setFormData] = useState({
    // Owner Details
    owner_name: '',
    email: '',
    phone: '',
    
    // Property Details
    property_type: 'residential',
    property_subtype: '',
    title: '',
    description: '',
    
    // Location
    address: '',
    locality: '',
    city: '',
    state: '',
    pincode: '',
    
    // Specifications - Area with unit support
    area_sqft: '',
    area_input_value: '',
    area_input_unit: 'sqft',
    bedrooms: '',
    bathrooms: '',
    balconies: '',
    floor: '',
    total_floors: '',
    parking: '',
    facing: '',
    age: '',
    
    // Pricing
    price: '',
    price_negotiable: true,
    listing_type: typeFromUrl,
    
    // Features
    amenities: '',
    highlights: ''
  });

  // Listing quota info
  const [quota, setQuota] = useState(null);
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      apiClient.get('/marketplace/listing-quota')
        .then(r => setQuota(r.data))
        .catch(() => {});
    }
  }, []);

  // Form validation errors
  const [errors, setErrors] = useState({});

  // Restore draft after login redirect
  useEffect(() => {
    const draft = getDraft('list_property');
    if (draft && draft.formData) {
      setFormData(prev => ({ ...prev, ...draft.formData }));
      clearDraft();
      toast.info('Your draft listing has been restored.');
    }
  }, []);

  const currentSubtypes = propertySubtypes[formData.property_type] || [];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validate phone format (Indian mobile)
  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length >= 10;
  };

  // Validate Step 1
  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.owner_name.trim()) {
      newErrors.owner_name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    if (!formData.property_subtype) {
      newErrors.property_subtype = 'Please select property subtype';
    }
    
    if (!formData.title.trim()) {
      newErrors.title = 'Property title is required';
    }
    
    if (!formData.area_sqft || parseFloat(formData.area_sqft) <= 0) {
      newErrors.area_sqft = 'Please enter valid area';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Step 2
  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    if (!formData.locality.trim()) {
      newErrors.locality = 'Locality is required';
    }
    
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    
    if (!formData.pincode.trim()) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Please enter valid 6-digit pincode';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Please enter valid price';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle step navigation with validation
  const handleNextStep = () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep(2);
      } else {
        toast.error('Please fill in all required fields correctly');
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStep(3);
      } else {
        toast.error('Please fill in all required fields correctly');
      }
    } else if (step === 3) {
      // Media step - images are optional but recommended
      if (propertyImages.length === 0) {
        toast.info('Adding photos increases your chances of getting inquiries!');
      }
      setStep(4);
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.owner_name || !formData.email || !formData.phone) {
      toast.error('Please fill in your contact details');
      return;
    }
    if (!formData.title || !formData.property_subtype || !formData.city || !formData.price) {
      toast.error('Please fill in all required property details');
      return;
    }

    // Auth gate: save draft and redirect if not logged in
    const token = getAuthToken();
    if (!token) {
      saveDraft('list_property', formData, '/list-property');
      setPendingAction({ type: 'list_property', returnPath: '/list-property' });
      toast.info('Please log in to submit your listing. Your progress has been saved.');
      navigate('/login', { state: { from: '/list-property', pendingAction: 'list_property' } });
      return;
    }

    setLoading(true);
    try {
      // Create FormData for file uploads
      const submitData = new FormData();
      
      // Add form fields
      submitData.append('owner_name', formData.owner_name);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('property_type', formData.property_type);
      submitData.append('property_subtype', formData.property_subtype);
      submitData.append('title', formData.title);
      submitData.append('description', formData.description || '');
      submitData.append('address', formData.address || '');
      submitData.append('locality', formData.locality || '');
      submitData.append('city', formData.city);
      submitData.append('state', formData.state || '');
      submitData.append('pincode', formData.pincode || '');
      submitData.append('area_sqft', formData.area_sqft || '0');
      submitData.append('area_input_value', formData.area_input_value || '');
      submitData.append('area_input_unit', formData.area_input_unit || 'sqft');
      submitData.append('bedrooms', formData.bedrooms || '');
      submitData.append('bathrooms', formData.bathrooms || '');
      submitData.append('price', formData.price || '0');
      submitData.append('listing_type', formData.listing_type);
      submitData.append('amenities', formData.amenities || '');
      submitData.append('enable_boost', enableBoost ? 'true' : 'false');
      if (enableBoost && selectedBoost) {
        submitData.append('boost_tier', selectedBoost);
      }
      
      // Add property images
      propertyImages.forEach((img) => {
        submitData.append('images', img.file);
      });
      
      // Add floor plans
      floorPlans.forEach((fp) => {
        submitData.append('floor_plans', fp.file);
      });
      
      // Add brochures
      brochures.forEach((br) => {
        submitData.append('brochures', br.file);
      });

      const response = await apiClient.post('/properties/list-request', submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(response.data.message || 'Property listing submitted successfully!');
      setStep(5); // Success step
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="list-property-page">
      {/* Hero Header */}
      <div className="bg-gradient-to-b from-slate-950 to-slate-900 text-white pt-24 pb-14">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            {formData.listing_type === 'rent' ? 'Find Tenants Fast' : formData.listing_type === 'pg' ? 'PG Listing' : 'Zero Brokerage'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 tracking-tight" data-testid="list-property-title">
            {formData.listing_type === 'rent' ? 'List Your Property for Rent' : 
             formData.listing_type === 'pg' ? 'List Your PG / Hostel' :
             formData.listing_type === 'lease' ? 'List for Lease' :
             'Sell Your Property'}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            {formData.listing_type === 'rent' 
              ? 'Reach thousands of verified tenants looking for homes in your area.'
              : 'Reach thousands of verified buyers and tenants. Get the best value for your property.'}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-6">
        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 md:p-8">

        {/* Listing Quota Banner */}
        {quota && (
          <div className={`mb-6 rounded-xl border p-4 flex items-center justify-between ${
            !quota.can_create ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'
          }`} data-testid="listing-quota-banner">
            <div className="flex items-center gap-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${!quota.can_create ? 'bg-red-100' : 'bg-emerald-100'}`}>
                <span className="text-sm font-bold">{quota.active_count}/{quota.max_active}</span>
              </div>
              <div>
                <p className="text-sm font-medium">{quota.plan_name === 'free_user' ? 'Free Plan' : 'Seller Basic'} &middot; {quota.listing_validity_days}-day validity</p>
                {!quota.can_create && (
                  <p className="text-xs text-red-600">You've reached your listing limit. Upgrade your plan to add more.</p>
                )}
              </div>
            </div>
            {quota.plan_name === 'free_user' && (
              <Button size="sm" className="rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs h-8" 
                onClick={() => navigate('/sell/individual#pricing')}
                data-testid="upgrade-from-form-btn"
              >
                Upgrade
              </Button>
            )}
          </div>
        )}

        {/* Quick Listing Type Selector */}
        <div className="flex flex-wrap justify-center gap-3 mb-8" data-testid="listing-type-selector">
          {[
            { value: 'sale', label: 'Sell', desc: 'Sell your property', color: 'emerald' },
            { value: 'rent', label: 'Rent Out', desc: 'Rent your property', color: 'blue' },
            { value: 'lease', label: 'Lease', desc: 'Long-term lease', color: 'violet' },
            { value: 'pg', label: 'PG / Hostel', desc: 'Paying Guest', color: 'amber' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => updateField('listing_type', opt.value)}
              className={`flex flex-col items-center px-6 py-4 rounded-xl border-2 font-medium transition-all min-w-[100px] ${
                formData.listing_type === opt.value
                  ? 'border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-900/20 scale-105'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400 hover:shadow-md'
              }`}
              data-testid={`listing-type-${opt.value}`}
            >
              <span className="text-sm font-semibold">{opt.label}</span>
              <span className={`text-[11px] mt-0.5 ${formData.listing_type === opt.value ? 'text-slate-300' : 'text-slate-400'}`}>{opt.desc}</span>
            </button>
          ))}
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-10 overflow-x-auto pb-2">
          {[
            { num: 1, label: 'Details' },
            { num: 2, label: 'Location & Price' },
            { num: 3, label: 'Photos & Media' },
            { num: 4, label: 'Boost (Optional)' },
            { num: 5, label: 'Complete' }
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center min-w-fit">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-medium transition-all ${
                  step > s.num
                    ? 'bg-emerald-500 text-white'
                    : step === s.num 
                    ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/20' 
                    : 'bg-slate-100 text-slate-400'
                }`}>
                  {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 whitespace-nowrap ${step >= s.num ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{s.label}</span>
              </div>
              {idx < 4 && (
                <div className={`w-12 md:w-16 h-1 mx-1 md:mx-2 rounded flex-shrink-0 ${
                  step > s.num ? 'bg-primary' : 'bg-gray-200'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Property Details */}
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="step-1-form">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <Building2 className="w-6 h-6 text-primary" />
              Property Details
            </h2>
            
            {/* Contact Info */}
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Your Name *</Label>
                <Input 
                  placeholder="Full Name"
                  value={formData.owner_name}
                  onChange={(e) => updateField('owner_name', e.target.value)}
                  className={errors.owner_name ? 'border-red-500' : ''}
                />
                {errors.owner_name && <p className="text-red-500 text-xs mt-1">{errors.owner_name}</p>}
              </div>
              <div>
                <Label>Email *</Label>
                <Input 
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <Label>Phone *</Label>
                <Input 
                  placeholder="+91 XXXXX XXXXX"
                  value={formData.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className={errors.phone ? 'border-red-500' : ''}
                />
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Property Type */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Property Type *</Label>
                <Select value={formData.property_type} onValueChange={(v) => updateField('property_type', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="agricultural">Agricultural</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Property Subtype *</Label>
                <Select value={formData.property_subtype} onValueChange={(v) => updateField('property_subtype', v)}>
                  <SelectTrigger className={errors.property_subtype ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select subtype" />
                  </SelectTrigger>
                  <SelectContent>
                    {currentSubtypes.map(sub => (
                      <SelectItem key={sub.value} value={sub.value}>{sub.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.property_subtype && <p className="text-red-500 text-xs mt-1">{errors.property_subtype}</p>}
              </div>
            </div>

            {/* Title & Description */}
            <div className="mb-6">
              <Label>Property Title *</Label>
              <Input 
                placeholder="e.g., Luxurious 3BHK in Jubilee Hills"
                value={formData.title}
                onChange={(e) => updateField('title', e.target.value)}
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            </div>
            <div className="mb-6">
              <Label>Description</Label>
              <Textarea 
                placeholder="Describe your property - features, USPs, nearby landmarks..."
                rows={4}
                value={formData.description}
                onChange={(e) => updateField('description', e.target.value)}
              />
            </div>

            {/* Specifications */}
            <h3 className="font-semibold mb-4">Specifications</h3>
            
            {/* Area Input with Unit Conversion */}
            <div className="mb-6">
              <AreaInput
                value={formData.area_input_value}
                unit={formData.area_input_unit}
                onChange={({ value, unit, sqftValue, isValid }) => {
                  updateField('area_input_value', value);
                  updateField('area_input_unit', unit);
                  updateField('area_sqft', sqftValue || '');
                }}
                propertyType={formData.property_subtype}
                showPreview={true}
                required={true}
                error={errors.area_sqft}
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {formData.property_type === 'residential' && (
                <>
                  <div>
                    <Label>Bedrooms</Label>
                    <Select value={formData.bedrooms} onValueChange={(v) => updateField('bedrooms', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="BHK" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} BHK</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Bathrooms</Label>
                    <Select value={formData.bathrooms} onValueChange={(v) => updateField('bathrooms', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Bath" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Parking</Label>
                    <Select value={formData.parking} onValueChange={(v) => updateField('parking', v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Parking" />
                      </SelectTrigger>
                      <SelectContent>
                        {[0,1,2,3,4].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            <Button onClick={handleNextStep} className="w-full" size="lg">
              Continue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Step 2: Location & Pricing */}
        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="step-2-form">
            <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-primary" />
              Location & Pricing
            </h2>
            
            {/* Location */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Address *</Label>
                <Input 
                  placeholder="Building/House No., Street"
                  value={formData.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  className={errors.address ? 'border-red-500' : ''}
                />
                {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
              </div>
              <div>
                <Label>Locality *</Label>
                <Input 
                  placeholder="e.g., Jubilee Hills"
                  value={formData.locality}
                  onChange={(e) => updateField('locality', e.target.value)}
                  className={errors.locality ? 'border-red-500' : ''}
                />
                {errors.locality && <p className="text-red-500 text-xs mt-1">{errors.locality}</p>}
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>City *</Label>
                <LocationSearch 
                  placeholder="Search city..."
                  value={formData.city}
                  onChange={(val) => updateField('city', val)}
                  types="city,town"
                  className={`mt-1 ${errors.city ? '[&_input]:border-red-500' : ''}`}
                  data-testid="list-property-city"
                  onLocationSelect={(location) => {
                    if (location?.state) updateField('state', location.state);
                    if (location?.pincode) updateField('pincode', location.pincode);
                  }}
                />
                {errors.city && <p className="text-red-500 text-xs mt-1">{errors.city}</p>}
              </div>
              <div>
                <Label>State *</Label>
                <LocationSearch 
                  placeholder="Search state..."
                  value={formData.state}
                  onChange={(val) => updateField('state', val)}
                  types="state"
                  className={`mt-1 ${errors.state ? '[&_input]:border-red-500' : ''}`}
                  data-testid="list-property-state"
                />
                {errors.state && <p className="text-red-500 text-xs mt-1">{errors.state}</p>}
              </div>
              <div>
                <Label>Pincode *</Label>
                <Input 
                  placeholder="500033"
                  value={formData.pincode}
                  onChange={(e) => updateField('pincode', e.target.value)}
                  className={errors.pincode ? 'border-red-500' : ''}
                />
                {errors.pincode && <p className="text-red-500 text-xs mt-1">{errors.pincode}</p>}
              </div>
            </div>

            {/* Pricing */}
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <IndianRupee className="w-5 h-5" />
              Pricing
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div>
                <Label>Price (₹) *</Label>
                <Input 
                  type="number"
                  placeholder={formData.listing_type === 'rent' || formData.listing_type === 'pg' ? 'Monthly Rent' : 'Total Price'}
                  value={formData.price}
                  onChange={(e) => updateField('price', e.target.value)}
                  className={errors.price ? 'border-red-500' : ''}
                />
                {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
              </div>
              <div className="flex items-center pt-6">
                <Checkbox 
                  id="negotiable"
                  checked={formData.price_negotiable}
                  onCheckedChange={(v) => updateField('price_negotiable', v)}
                />
                <Label htmlFor="negotiable" className="cursor-pointer ml-2">Price is negotiable</Label>
              </div>
            </div>

            {/* Amenities */}
            <div className="mb-6">
              <Label>Amenities (comma separated)</Label>
              <Input 
                placeholder="Swimming Pool, Gym, Club House, Power Backup..."
                value={formData.amenities}
                onChange={(e) => updateField('amenities', e.target.value)}
              />
            </div>
            <div className="mb-6">
              <Label>Highlights (comma separated)</Label>
              <Input 
                placeholder="Corner Unit, Vastu Compliant, Near Metro..."
                value={formData.highlights}
                onChange={(e) => updateField('highlights', e.target.value)}
              />
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleNextStep} className="flex-1">
                Continue <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Photos & Media Upload */}
        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="step-3-media">
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Camera className="w-6 h-6 text-primary" />
              Photos & Media
            </h2>
            <p className="text-gray-600 mb-6">
              Add photos to showcase your property. Listings with photos get 10x more inquiries!
            </p>

            {/* Property Images */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <ImageIcon className="w-5 h-5 text-primary" />
                    Property Photos
                  </h3>
                  <p className="text-sm text-gray-500">Upload up to {MAX_IMAGES} images (JPG, PNG, WebP - Max 5MB each)</p>
                </div>
                <span className="text-sm font-medium text-primary">{propertyImages.length}/{MAX_IMAGES}</span>
              </div>
              
              <div 
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary transition-colors cursor-pointer bg-gray-50/50"
                onClick={() => imageInputRef.current?.click()}
                data-testid="image-upload-zone"
              >
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-1 font-medium">Click to upload property photos</p>
                <p className="text-xs text-gray-400">Drag and drop or click to browse</p>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  multiple
                  className="hidden"
                  onChange={handleImageUpload}
                  data-testid="image-file-input"
                />
              </div>
              
              {propertyImages.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                  {propertyImages.map((img, idx) => (
                    <div key={img.id} className="relative group aspect-square">
                      <img 
                        src={img.preview} 
                        alt={`Property ${idx + 1}`}
                        className="w-full h-full object-cover rounded-lg border"
                      />
                      {idx === 0 && (
                        <span className="absolute bottom-1 left-1 bg-primary text-white text-xs px-2 py-0.5 rounded">
                          Cover
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                        data-testid={`remove-image-${idx}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Floor Plans */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    Floor Plans (Optional)
                  </h3>
                  <p className="text-sm text-gray-500">Upload up to {MAX_FLOOR_PLANS} floor plans (Images or PDF)</p>
                </div>
                <span className="text-sm font-medium text-blue-600">{floorPlans.length}/{MAX_FLOOR_PLANS}</span>
              </div>
              
              <div 
                className="border-2 border-dashed border-blue-200 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer bg-blue-50/30"
                onClick={() => floorPlanInputRef.current?.click()}
                data-testid="floorplan-upload-zone"
              >
                <FileText className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Click to upload floor plans</p>
                <input
                  ref={floorPlanInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                  multiple
                  className="hidden"
                  onChange={handleFloorPlanUpload}
                />
              </div>
              
              {floorPlans.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {floorPlans.map((fp) => (
                    <div key={fp.id} className="relative group">
                      {fp.preview ? (
                        <img 
                          src={fp.preview} 
                          alt={fp.name}
                          className="w-24 h-24 object-cover rounded-lg border"
                        />
                      ) : (
                        <div className="w-24 h-24 bg-blue-50 rounded-lg border border-blue-200 flex flex-col items-center justify-center p-2">
                          <FileText className="w-8 h-8 text-blue-500 mb-1" />
                          <span className="text-xs text-gray-600 truncate w-full text-center">{fp.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => removeFloorPlan(fp.id)}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Brochures */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Brochures (Optional)
                  </h3>
                  <p className="text-sm text-gray-500">Upload up to {MAX_BROCHURES} PDF brochures (Max 10MB each)</p>
                </div>
                <span className="text-sm font-medium text-amber-600">{brochures.length}/{MAX_BROCHURES}</span>
              </div>
              
              <div 
                className="border-2 border-dashed border-amber-200 rounded-xl p-6 text-center hover:border-amber-400 transition-colors cursor-pointer bg-amber-50/30"
                onClick={() => brochureInputRef.current?.click()}
                data-testid="brochure-upload-zone"
              >
                <FileText className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-gray-600 text-sm">Click to upload brochures (PDF)</p>
                <input
                  ref={brochureInputRef}
                  type="file"
                  accept="application/pdf"
                  multiple
                  className="hidden"
                  onChange={handleBrochureUpload}
                />
              </div>
              
              {brochures.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4">
                  {brochures.map((br) => (
                    <div key={br.id} className="relative group">
                      <div className="px-4 py-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-amber-600" />
                        <span className="text-sm text-gray-700 max-w-[150px] truncate">{br.name}</span>
                        <button
                          type="button"
                          onClick={() => removeBrochure(br.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tips */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <h4 className="font-medium text-green-800 mb-2">Tips for great photos:</h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Use natural lighting - shoot during daytime</li>
                <li>• Capture all rooms and outdoor spaces</li>
                <li>• Keep the space clean and decluttered</li>
                <li>• Take horizontal (landscape) photos</li>
              </ul>
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleNextStep} className="flex-1">
                {propertyImages.length === 0 ? 'Skip for now' : 'Continue'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 4: Boost Options */}
        {step === 4 && (
          <div className="space-y-6" data-testid="step-3-boost">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Rocket className="w-6 h-6 text-primary" />
                Boost Your Property
              </h2>
              <p className="text-gray-600 mb-6">
                Get up to 4x more visibility and sell/rent faster with our boost options.
              </p>

              {/* Why Boost Section */}
              <div className="bg-gradient-to-r from-primary/5 to-amber-50 rounded-xl p-6 mb-8">
                <h3 className="font-semibold mb-4">Why Boost Your Listing?</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3">
                    <Eye className="w-5 h-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Higher Visibility</p>
                      <p className="text-xs text-gray-600">Up to 4x more views on your listing</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Target className="w-5 h-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Targeted Reach</p>
                      <p className="text-xs text-gray-600">Reach buyers matching your property</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-primary mt-1" />
                    <div>
                      <p className="font-medium text-sm">Faster Results</p>
                      <p className="text-xs text-gray-600">Sell/rent up to 3x faster</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enable Boost Toggle */}
              <div className="flex items-center gap-3 mb-6 p-4 border rounded-xl">
                <Checkbox 
                  id="enable-boost"
                  checked={enableBoost}
                  onCheckedChange={setEnableBoost}
                />
                <Label htmlFor="enable-boost" className="cursor-pointer flex-1">
                  <span className="font-medium">Enable Property Boost</span>
                  <span className="text-sm text-gray-500 block">Get more inquiries and close deals faster</span>
                </Label>
              </div>

              {/* Boost Tiers */}
              {enableBoost && (
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(boostTiers).map(([key, tier]) => (
                    <div 
                      key={key}
                      onClick={() => setSelectedBoost(key)}
                      className={`relative p-6 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedBoost === key 
                          ? `${tier.borderColor} ${tier.bgColor} shadow-lg scale-105` 
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                      data-testid={`boost-tier-${key}`}
                    >
                      {tier.popular && (
                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs px-3 py-1 rounded-full">
                          Most Popular
                        </span>
                      )}
                      <tier.icon className={`w-8 h-8 ${tier.color} mb-3`} />
                      <h4 className="font-bold text-lg mb-1">{tier.name}</h4>
                      <p className="text-2xl font-bold mb-1">₹{tier.price.toLocaleString()}</p>
                      <p className="text-sm text-gray-500 mb-4">{tier.duration} • {tier.multiplier} visibility</p>
                      <ul className="space-y-2">
                        {tier.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm">
                            <CheckCircle className={`w-4 h-4 ${tier.color} flex-shrink-0 mt-0.5`} />
                            {feature}
                          </li>
                        ))}
                        {tier.features.length > 4 && (
                          <li className="text-sm text-gray-500">+{tier.features.length - 4} more features</li>
                        )}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-4">
              <Button variant="outline" onClick={() => setStep(3)} className="flex-1">
                Back
              </Button>
              <Button onClick={handleSubmit} disabled={loading} className="flex-1">
                {loading ? 'Submitting...' : enableBoost && selectedBoost 
                  ? `Submit & Pay ₹${boostTiers[selectedBoost]?.price.toLocaleString()}` 
                  : 'Submit Listing'}
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Success */}
        {step === 5 && (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center" data-testid="step-5-success">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold mb-4">Listing Submitted Successfully!</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Our team will review your property and publish it within 24 hours. 
              You&apos;ll receive a confirmation email shortly.
            </p>
            
            {propertyImages.length > 0 && (
              <div className="bg-blue-50 rounded-xl p-4 mb-4 max-w-md mx-auto">
                <p className="text-sm text-blue-700">
                  <Camera className="w-4 h-4 inline mr-1" />
                  {propertyImages.length} photo{propertyImages.length > 1 ? 's' : ''} uploaded
                </p>
              </div>
            )}
            
            {enableBoost && selectedBoost && (
              <div className="bg-primary/5 rounded-xl p-6 mb-8 max-w-md mx-auto">
                <p className="font-medium mb-2">🚀 {boostTiers[selectedBoost]?.name} Activated</p>
                <p className="text-sm text-gray-600">
                  Your property will get {boostTiers[selectedBoost]?.multiplier} visibility for {boostTiers[selectedBoost]?.duration}
                </p>
              </div>
            )}
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => navigate('/real-estate')}>
                Browse Properties
              </Button>
              <Button onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        )}

      </div>
      </div>
    </div>
  );
};

export default ListProperty;
